import React from 'react';

import { cn } from '../../../shadcn/lib/utils';

import { IMultiSelectOptionProps, IMultiSelectProps, IUseMultiSelectStateProps } from './types';

const getTriggerStyles = (className?: string, error?: boolean) =>
  cn(
    '!h-[29px] w-full cursor-pointer focus-visible:ring-indigo-700 bg-neutral-0 border-neutral-500 text-typography hover:bg-neutral-0 hover:text-typography',
    error ? 'border-error' : '',
    className ?? ''
  );

const isOptionElement = (child: React.ReactNode): child is React.ReactElement<IMultiSelectOptionProps> =>
  React.isValidElement<IMultiSelectOptionProps>(child) && typeof child.props?.value === 'string';

const useOptionsMetadata = (children: React.ReactNode) =>
  React.useMemo(() => {
    const map: Record<string, React.ReactNode> = {};
    let firstOptionValue: string | undefined;
    let firstEnabledOptionValue: string | undefined;

    React.Children.forEach(children, (child) => {
      if (!isOptionElement(child)) return;
      const { value, children: optionLabel, disabled } = child.props;
      map[value] = optionLabel;
      firstOptionValue ??= value;
      if (!disabled && firstEnabledOptionValue === undefined) firstEnabledOptionValue = value;
    });

    return { firstOptionValue: firstEnabledOptionValue ?? firstOptionValue, optionLabelMap: map };
  }, [children]);

const useMultiSelectState = ({
  defaultValue,
  maxVisible,
  onValueChange,
  optionLabelMap,
  value,
}: IUseMultiSelectStateProps) => {
  const [internalValue, setInternalValue] = React.useState<string[]>(defaultValue);
  const selectedValues = value ?? internalValue;

  const updateValue = (next: string[]) => {
    if (value === undefined) setInternalValue(next);
    onValueChange(next);
  };

  const toggleOption = (optionValue: string) => {
    const exists = selectedValues.includes(optionValue);
    const next = exists ? selectedValues.filter((v) => v !== optionValue) : [...selectedValues, optionValue];
    updateValue(next);
  };

  const removeOption = (optionValue: string) => updateValue(selectedValues.filter((v) => v !== optionValue));

  const removeRemainingOptions = () => {
    const newSelectedValues = selectedValues.slice(0, maxVisible);
    updateValue(newSelectedValues);
  };

  const isSelected = (optionValue: string) => selectedValues.includes(optionValue);
  const getLabel = (optionValue: string) => optionLabelMap[optionValue] ?? optionValue;

  return { getLabel, isSelected, removeOption, removeRemainingOptions, selectedValues, toggleOption };
};

const useMultiSelectSetup = ({
  children,
  className,
  defaultValue,
  error,
  id,
  maxVisible,
  onValueChange,
  value,
}: IMultiSelectProps & { readonly defaultValue: string[]; readonly maxVisible: number }) => {
  const generatedSelectId = React.useId();
  const selectId = id || generatedSelectId;
  const labelId = React.useId();
  const commandListRef = React.useRef<HTMLDivElement>(null);
  const triggerStyles = getTriggerStyles(className, error);
  const [open, setOpen] = React.useState(false);
  const { optionLabelMap, firstOptionValue } = useOptionsMetadata(children);
  const [commandValue, setCommandValue] = React.useState<string | undefined>(firstOptionValue);
  const { getLabel, isSelected, removeOption, removeRemainingOptions, selectedValues, toggleOption } =
    useMultiSelectState({ defaultValue, maxVisible, onValueChange, optionLabelMap, value });
  const contextValue = React.useMemo(
    () => ({ getLabel, isSelected, removeOption, selectedValues, toggleOption }),
    [getLabel, isSelected, removeOption, selectedValues, toggleOption]
  );

  React.useEffect(() => {
    if (!open) return;

    setCommandValue(firstOptionValue);
    const focusTimeout = globalThis.setTimeout(() => commandListRef.current?.focus(), 0);

    return () => globalThis.clearTimeout(focusTimeout);
  }, [firstOptionValue, open]);

  return {
    commandListRef,
    commandValue,
    contextValue,
    getLabel,
    labelId,
    open,
    removeOption,
    removeRemainingOptions,
    selectId,
    selectedValues,
    setCommandValue,
    setOpen,
    triggerStyles,
  };
};

export { getTriggerStyles, useMultiSelectSetup, useMultiSelectState, useOptionsMetadata };
